import { inject, Injectable } from '@angular/core';
import { SearchODataOptions, SiteDriveOdataItem } from '@common/models';
import { SortOrderByPipe } from '@common/pipes';
import { SiteItemsODataService } from '@common/services';
import { getUniqueListByProp } from '@common/services/smart-search';
import { SharePointTag, SharePointTagsMap } from '@common/services/smart-search/sharePoint/smart-search-tags-sharepoint.model';
import SmartSearchTemplatesBase from '@common/services/smart-search/smart-search-template-base';
import { smartSearchModelFactory } from '@common/utils/functions/search';
import { ModelTemplate, SmartSearchState } from 'mbs-ui-kit';
import { asapScheduler, Observable, scheduled } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable()
export class SmartSearchModelTemplateSharePointService extends SmartSearchTemplatesBase {
  private readonly siteItemsService = inject(SiteItemsODataService);
  private readonly sortOrderByPipe = inject<SortOrderByPipe<SiteDriveOdataItem>>(SortOrderByPipe);

  readonly #fileName = SharePointTagsMap.get(SharePointTag.FileName);
  readonly #mixinName = SharePointTagsMap.get(SharePointTag.Mixin);
  readonly #siteName = SharePointTagsMap.get(SharePointTag.SiteName);

  public readonly SiteNameTag: ModelTemplate<SiteDriveOdataItem> = {
    tag: this.#siteName.tag,
    items: (state): Observable<SiteDriveOdataItem[]> => {
      const term = state.leftCaretValue;
      const options: SearchODataOptions = this.getSearchODataOptions({
        filter: this.siteItemsService.updateFilter(smartSearchModelFactory(this.#siteName.tag, term)) || '',
        orderBy: ''
      });

      return scheduled(
        this.siteItemsService.siteItems(options).pipe(
          take(1),
          map((res) => res.data),
          map((sites) => {
            const filtered = sites.filter((site) => site.Name != null && site.Name.toLowerCase().includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#siteName.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, this.#siteName.prop);
          })
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ Name }): string => Name,
    addGroupBrackets: true
  };

  public readonly FileNameTag: ModelTemplate<SiteDriveOdataItem> = {
    tag: this.#fileName.tag,
    items: (state): Observable<SiteDriveOdataItem[]> => {
      const term = state.leftCaretValue;
      const options: SearchODataOptions = this.getSearchODataOptions({
        filter: this.getFileFilter(state, term) || '',
        orderBy: 'Type asc'
      });

      return scheduled(
        this.siteItemsService.findDriveItemsOfSiteList(options).pipe(
          take(1),
          map((res) => res.data),
          map((files) => {
            const filtered = files.filter((file) => file.Name != null && file.Name.toLowerCase().includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#fileName.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, this.#fileName.prop);
          })
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ Name }): string => Name,
    addGroupBrackets: true
  };

  private getFileFilter(state: SmartSearchState, term: string): string {
    if (state.usedTags[0] === this.#siteName.tag.toLowerCase()) {
      // we need check first position `SiteName` tag for search file in site
      const smartSearchObjs = [];
      const value = state.model.find((i) => i['tagValue'] === this.#siteName.tag)['fields'][0].value;

      smartSearchObjs.push(smartSearchModelFactory(this.#mixinName.tag, value));
      smartSearchObjs.push(smartSearchModelFactory(this.#fileName.tag, term));

      return this.siteItemsService.updateFilter(smartSearchObjs);
    } else {
      return this.siteItemsService.updateFilter(smartSearchModelFactory(this.#fileName.tag, term));
    }
  }

  private getSearchODataOptions(options: { filter: string; orderBy: string }): SearchODataOptions {
    const { filter, orderBy } = options || {};

    return {
      filter,
      orderBy,
      page: 1,
      pageSize: 50
    };
  }
}
